import { useNuxtApp } from '#app'
import { useMutation } from '@tanstack/vue-query'
import ToastAction from '~/components/toast/ToastAction.vue'
import type { MutationOptions } from '@tanstack/query-core'
import type { QueryClient } from '@tanstack/vue-query'

interface ToastMessages {
  pending?: string
  success?: string | ((data: any) => string)
  error?: string
}

// Define the mutation context type
interface MutationContext {
  toastPromise?: Promise<any>
}

export function generateErrorHtmlList(errorData: Record<string, string[]>): string {
  let html = '<ul>'
  for (const [key, messages] of Object.entries(errorData)) {
    html += messages.map((message) => `<li>${message}</li>`).join('')
  }
  html += '</ul>'
  return html
}

export function useMutationWithToast<TData, TVariables, TContext = unknown>(
  mutationOptions: MutationOptions<TData, Error, TVariables>,
  queryClient?: QueryClient,
  toastMessages: ToastMessages = {},
) {
  const { $toast } = useNuxtApp()
  const { getApiErrorMessage } = useApiError()
  const toastId = ref()

  return useMutation(
    {
      ...mutationOptions,
      onMutate: async (variables) => {
        // Show a pending toast notification with a customizable message
        toastId.value = Math.random()
        $toast.loading(ToastAction, {
          toastId: toastId.value,
          data: {
            message: toastMessages.pending || 'Action is pending...',
          },
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          type: 'loading',
          isLoading: true,
        })

        // Call the user's onMutate if it exists
        if (mutationOptions?.onMutate) {
          await mutationOptions.onMutate(variables)
        }

        return
      },
      onSuccess: async (data, variables, context) => {
        // Call the user's onSuccess if it exists
        const successToast = () => {
          $toast.update(toastId.value, {
            toastId: toastId.value,
            render: ToastAction,
            data: {
              message:
                typeof toastMessages.success === 'function'
                  ? toastMessages.success(data)
                  : toastMessages.success || 'Action successful!',
            },
            autoClose: true,
            closeOnClick: true,
            closeButton: false,
            type: 'success',
            isLoading: false,
          })
          toastId.value = undefined
        }

        if (mutationOptions?.onSuccess) {
          const hasNavigation =
            mutationOptions.onSuccess?.toString().includes('navigateTo') ?? false

          if (hasNavigation) successToast()
          await mutationOptions.onSuccess(data, variables, context)
          if (!hasNavigation) successToast()
        } else {
          successToast()
        }
      },
      throwOnError: false,
      onError: (error, variables, context) => {
        let errorMessage: string | null = null
        const code = error.status || error.statusCode
        if (code < 500 && error.data) {
          try {
            errorMessage = getApiErrorMessage(error.data) || generateErrorHtmlList(error.data)
          } catch (e) {
            console.error('failed to generate html list', e)
          }
        }

        $toast.update(toastId.value, {
          toastId: toastId.value,
          render: ToastAction,
          data: {
            message: toastMessages.error || 'Action failed!',
            details: errorMessage,
          },
          autoClose: true,
          closeOnClick: true,
          closeButton: false,
          type: 'error',
          isLoading: false,
        })
        // Call the user's onError if it exists
        if (mutationOptions?.onError) {
          mutationOptions.onError(error, variables, context)
        }
        toastId.value = undefined
      },
    },
    queryClient,
  )
}
